import React, { useState } from 'react';
import { Building, Users, FileText, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BuildingData } from './Step3Buildings';
import { ClientData } from './Step4CSVImport';
import { Button } from '@/components/ui/button';

interface Step5ReviewProps {
  organizationData: {
    name: string;
    logoPreview: string;
    adminEmail: string;
    phone: string;
    address: string;
  };
  templatesData: {
    contractFile: File | null;
    billFile: File | null;
    useDefaults: { contract: boolean; bill: boolean };
  };
  buildings: BuildingData[];
  clientsData: {
    parsedData: ClientData[];
    options: {
      sendWelcomeEmails: boolean;
      autoGenerateClientNumbers: boolean;
      createAccountsImmediately: boolean;
    };
  };
}

export const Step5Review: React.FC<Step5ReviewProps> = ({
  organizationData,
  templatesData,
  buildings,
  clientsData,
}) => {
  const [showAllClients, setShowAllClients] = useState(false);
  const totalApartments = buildings.reduce((sum, b) => sum + b.apartment_count, 0);
  
  const clientsByBuilding = clientsData.parsedData.reduce((acc, client) => {
    acc[client.building_name] = (acc[client.building_name] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <CheckCircle2 className="h-8 w-8 text-primary" />
        <div>
          <h2 className="text-2xl font-bold text-foreground">Review & Confirm</h2>
          <p className="text-muted-foreground">Review all information before completing setup</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Organization</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              {organizationData.logoPreview ? (
                <img src={organizationData.logoPreview} alt="Logo" className="h-12 w-12 object-contain" />
              ) : (
                <div className="h-12 w-12 bg-muted rounded flex items-center justify-center">
                  <Building className="h-6 w-6 text-muted-foreground" />
                </div>
              )}
              <div>
                <p className="font-semibold">{organizationData.name}</p>
                <p className="text-sm text-muted-foreground">{organizationData.adminEmail}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Buildings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 bg-primary/10 rounded flex items-center justify-center">
                <Building className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{buildings.length}</p>
                <p className="text-sm text-muted-foreground">{totalApartments} total apartments</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Clients</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 bg-primary/10 rounded flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{clientsData.parsedData.length}</p>
                <p className="text-sm text-muted-foreground">Ready to import</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Templates Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            PDF Templates
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between py-2 border-b">
            <span className="text-sm">Contract Template</span>
            <span className="text-sm text-muted-foreground">
              {templatesData.useDefaults.contract 
                ? 'Using default template' 
                : templatesData.contractFile?.name || 'Not uploaded'}
            </span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm">Bill Template</span>
            <span className="text-sm text-muted-foreground">
              {templatesData.useDefaults.bill 
                ? 'Using default template' 
                : templatesData.billFile?.name || 'Not uploaded'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Buildings Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Buildings Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {buildings.map((building, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b last:border-0">
                <div>
                  <p className="font-medium">{building.name}</p>
                  {building.address && <p className="text-sm text-muted-foreground">{building.address}</p>}
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">{building.apartment_count} apartments</p>
                  {clientsByBuilding[building.name] && (
                    <p className="text-sm text-muted-foreground">
                      {clientsByBuilding[building.name]} clients
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Import Options */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Import Options</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className={`h-4 w-4 ${clientsData.options.sendWelcomeEmails ? 'text-green-500' : 'text-muted-foreground'}`} />
              <span>Send welcome emails to clients</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className={`h-4 w-4 ${clientsData.options.autoGenerateClientNumbers ? 'text-green-500' : 'text-muted-foreground'}`} />
              <span>Auto-generate client numbers</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className={`h-4 w-4 ${clientsData.options.createAccountsImmediately ? 'text-green-500' : 'text-muted-foreground'}`} />
              <span>Create accounts immediately</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Clients Preview */}
      {clientsData.parsedData.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">
              Clients Preview {showAllClients ? `(All ${clientsData.parsedData.length})` : '(First 10)'}
            </CardTitle>
            {clientsData.parsedData.length > 10 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAllClients(!showAllClients)}
              >
                {showAllClients ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-2" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-2" />
                    Show All
                  </>
                )}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-2 text-left">Meter ID</th>
                    <th className="px-4 py-2 text-left">Building</th>
                    <th className="px-4 py-2 text-left">Apt #</th>
                    <th className="px-4 py-2 text-left">Name</th>
                    <th className="px-4 py-2 text-left">Email</th>
              </tr>
            </thead>
            <tbody>
              {clientsData.parsedData
                .slice(0, showAllClients ? undefined : 10)
                .map((client, index) => (
                  <tr key={index} className="border-t">
                    <td className="px-4 py-2 font-mono text-xs">{client.meter_id}</td>
                    <td className="px-4 py-2">{client.building_name}</td>
                    <td className="px-4 py-2">{client.apartment_number}</td>
                    <td className="px-4 py-2">
                      {client.first_name || client.last_name 
                        ? `${client.first_name || ''} ${client.last_name || ''}`.trim()
                        : '-'}
                    </td>
                    <td className="px-4 py-2">{client.email || '-'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        </CardContent>
      </Card>
      )}
    </div>
  );
};
